import { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo, } from 'react';
import canUseDOM from '../../utils/canUseDOM';
import usePrevious from '../../utils/use-previous';
import useForceFocus from './useForceFocus';
var getCarouselSlides = function (currentContainerWidth, carouselInnerElement, noPadding) {
    var items = Array.from(carouselInnerElement.children);
    var carouselSlides = [];
    var slideIndex = 0;
    items.forEach(function (item, index) {
        var isLastItem = index === items.length - 1;
        if (!carouselSlides[slideIndex]) {
            carouselSlides[slideIndex] = {
                offset: 0,
                itemCount: 0,
                itemsWidth: 0,
            };
        }
        var itemWidth = item.getBoundingClientRect().width || item.offsetWidth;
        var itemMargin = noPadding || isLastItem ? 0 : 16;
        if (Math.round(itemWidth) > currentContainerWidth) {
            // eslint-disable-next-line no-console
            console.error("Item #".concat(index, " passed into Carousel is too wide for this breakpoint. Please adjust the width of the items."));
            return;
        }
        if (Math.round(carouselSlides[slideIndex].itemsWidth + itemWidth) <= currentContainerWidth) {
            carouselSlides[slideIndex].itemsWidth += itemWidth + itemMargin;
            carouselSlides[slideIndex].itemCount += 1;
        }
        else {
            slideIndex += 1;
            carouselSlides[slideIndex] = {
                offset: carouselSlides[slideIndex - 1].itemsWidth + carouselSlides[slideIndex - 1].offset,
                itemCount: 0,
                itemsWidth: 0,
            };
            carouselSlides[slideIndex].itemsWidth += itemWidth + itemMargin;
            carouselSlides[slideIndex].itemCount += 1;
        }
    });
    return carouselSlides;
};
var useCarousel = function (allChildren, selectedSlide, disableScrollTo, onSlideView, noPadding) {
    if (noPadding === void 0) { noPadding = false; }
    var carouselRef = useRef(null);
    var carouselInnerRef = useRef(null);
    var nextButtonRef = useRef(null);
    var previousButtonRef = useRef(null);
    var buttonRefs = useMemo(function () { return ({
        next: nextButtonRef,
        previous: previousButtonRef,
    }); }, []);
    var _a = useState(), buttonToFocus = _a[0], setButtonToFocus = _a[1];
    var touchStartRef = useRef();
    var scrollBarRef = useRef(null);
    var children = Array.isArray(allChildren) ? allChildren : [allChildren];
    var childrenCount = children.length;
    var _b = useState(false), shouldUpdateSlides = _b[0], setShouldUpdateSlides = _b[1];
    var _c = useState([]), slides = _c[0], setSlides = _c[1];
    var previousSlides = usePrevious(slides);
    var _d = useState(selectedSlide !== null && selectedSlide !== void 0 ? selectedSlide : 0), currentSlide = _d[0], setCurrentSlide = _d[1];
    var _e = useState([]), visibleElements = _e[0], setVisibleElements = _e[1];
    var _f = useState({ currentSlide: 0, visibleElements: [] }), reportedElements = _f[0], setReportedElements = _f[1];
    var previousVisibleElements = usePrevious(visibleElements);
    var _g = useForceFocus(), memoriseTabIndexes = _g.memoriseTabIndexes, forceCurrentSlideFocus = _g.forceCurrentSlideFocus;
    var _h = useState('start'), scrollingXStatus = _h[0], setScrollingXStatus = _h[1];
    // eslint-disable-next-line consistent-return
    var scrollToNextSlide = useCallback(function () {
        var _a, _b;
        if (scrollBarRef.current) {
            if (scrollingXStatus !== 'end') {
                var slide = slides[currentSlide];
                var scrollerElement = scrollBarRef.current.getScrollElement();
                scrollerElement === null || scrollerElement === void 0 ? void 0 : scrollerElement.scrollTo({
                    left: ((_a = slide === null || slide === void 0 ? void 0 : slide.offset) !== null && _a !== void 0 ? _a : 0) + ((_b = slide === null || slide === void 0 ? void 0 : slide.itemsWidth) !== null && _b !== void 0 ? _b : 0),
                    behavior: 'smooth',
                });
            }
        }
    }, [currentSlide, scrollingXStatus, slides]);
    // eslint-disable-next-line consistent-return
    var scrollToPreviousSlide = useCallback(function () {
        if (scrollBarRef.current) {
            if (scrollingXStatus !== 'start') {
                var previousSlide = slides[currentSlide - 1];
                var scrollerElement = scrollBarRef.current.getScrollElement();
                scrollerElement === null || scrollerElement === void 0 ? void 0 : scrollerElement.scrollTo({
                    left: (previousSlide === null || previousSlide === void 0 ? void 0 : previousSlide.offset) > 0 ? previousSlide.offset : 0,
                    behavior: 'smooth',
                });
            }
        }
    }, [currentSlide, scrollingXStatus, slides]);
    useLayoutEffect(function () {
        if (allChildren && carouselInnerRef.current) {
            // Recalculate slides when children change (and set it initially)
            setShouldUpdateSlides(true);
            memoriseTabIndexes(carouselInnerRef.current);
        }
    }, [allChildren, memoriseTabIndexes]);
    useEffect(function () {
        if (carouselInnerRef.current &&
            ((visibleElements === null || visibleElements === void 0 ? void 0 : visibleElements.toString()) !== (previousVisibleElements === null || previousVisibleElements === void 0 ? void 0 : previousVisibleElements.toString()) ||
                (slides.length && slides !== previousSlides))) {
            forceCurrentSlideFocus(carouselInnerRef.current, visibleElements);
        }
    }, [
        currentSlide,
        forceCurrentSlideFocus,
        previousSlides,
        slides,
        visibleElements,
        previousVisibleElements,
    ]);
    useEffect(function () {
        // Move the focus to the correct place after keyboard interaction
        if (buttonToFocus && buttonRefs[buttonToFocus].current) {
            buttonRefs[buttonToFocus].current.focus();
            setButtonToFocus(undefined);
        }
    }, [buttonRefs, buttonToFocus, currentSlide]);
    useEffect(function () {
        var validReport = visibleElements.toString() !== reportedElements.visibleElements.toString() ||
            currentSlide !== reportedElements.currentSlide;
        if (onSlideView && slides.length > 0 && validReport) {
            onSlideView(currentSlide, visibleElements);
            setReportedElements({ currentSlide: currentSlide, visibleElements: visibleElements });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSlide, visibleElements, slides.length, onSlideView]);
    useLayoutEffect(function () {
        var _a, _b;
        // Go back to the first slide when the window is resized and the number of slides change
        // We also want to make sure this doesn't get triggered on mount, since at that time we want it to keep the initial selectedSlide received
        if (previousSlides && previousSlides.length > 0) {
            setCurrentSlide(0);
            setScrollingXStatus('start');
            (_b = (_a = scrollBarRef.current) === null || _a === void 0 ? void 0 : _a.getScrollElement()) === null || _b === void 0 ? void 0 : _b.scrollTo({
                left: 0,
                behavior: 'smooth',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slides.length]);
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        var width = (_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth;
        // Update slides on resize (and set them initially)
        if (width && shouldUpdateSlides && carouselInnerRef.current) {
            setShouldUpdateSlides(false);
            var carouselSlides = getCarouselSlides(width, carouselInnerRef.current, noPadding);
            setSlides(carouselSlides);
            if (!selectedSlide) {
                setVisibleElements(Array.from(Array((_b = carouselSlides[0]) === null || _b === void 0 ? void 0 : _b.itemCount).fill(0), function (_, index) { return index; }));
            }
            // it should scroll only if disableScrollTo is false
            if (!disableScrollTo) {
                (_d = (_c = scrollBarRef === null || scrollBarRef === void 0 ? void 0 : scrollBarRef.current) === null || _c === void 0 ? void 0 : _c.getScrollElement()) === null || _d === void 0 ? void 0 : _d.scrollTo({
                    left: ((_e = carouselSlides[selectedSlide]) === null || _e === void 0 ? void 0 : _e.offset) || 0,
                    behavior: 'smooth',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSlide, memoriseTabIndexes, shouldUpdateSlides, noPadding]);
    useEffect(function () {
        // Set up resize handler
        var resizeHandler = function () {
            if (!shouldUpdateSlides)
                setShouldUpdateSlides(true);
        };
        if (canUseDOM()) {
            window.addEventListener('resize', resizeHandler);
            return function () { return window.removeEventListener('resize', resizeHandler); };
        }
        return undefined;
    }, [shouldUpdateSlides]);
    var handleKeyDown = useCallback(function (e) {
        switch (e.key) {
            case 'ArrowLeft':
            case 'Left':
                e.preventDefault();
                setButtonToFocus('previous');
                scrollToPreviousSlide();
                break;
            case 'ArrowRight':
            case 'Right':
                e.preventDefault();
                setButtonToFocus('next');
                scrollToNextSlide();
                break;
            default:
        }
    }, [scrollToNextSlide, scrollToPreviousSlide]);
    var handleTouchStart = useCallback(function (e) {
        var _a;
        if (e.touches.length === 1) {
            touchStartRef.current = (_a = e.touches.item(0)) === null || _a === void 0 ? void 0 : _a.clientX;
        }
    }, []);
    var handleTouchEnd = useCallback(function (e) {
        var _a;
        var start = touchStartRef.current;
        var end = (_a = e.changedTouches.item(0)) === null || _a === void 0 ? void 0 : _a.clientX;
        var touchPointsAreValidNumbers = typeof start === 'number' && typeof end === 'number';
        if (touchPointsAreValidNumbers) {
            if (end > start) {
                scrollToPreviousSlide();
            }
            if (end < start) {
                scrollToNextSlide();
            }
        }
        touchStartRef.current = undefined;
    }, [scrollToPreviousSlide, scrollToNextSlide]);
    var updateVisibleSlides = useCallback(function (_a) {
        var _b;
        var scrollLeft = _a.scrollLeft, scrollWidth = _a.scrollWidth, clientWidth = _a.clientWidth;
        var percentageSlideBeginning = scrollLeft / scrollWidth;
        var percentageSlideEnding = (scrollLeft + clientWidth) / scrollWidth;
        var visibleEls = [];
        var childrenPositionAgg = 0;
        var carouselElements = Array.from(((_b = carouselInnerRef.current) === null || _b === void 0 ? void 0 : _b.children) || []);
        var carouselWidth = carouselElements.reduce(function (acc, child) {
            return acc + child.clientWidth;
        }, 0);
        carouselElements.forEach(function (child, i) {
            var elementBeginning = childrenPositionAgg / carouselWidth;
            var elementEnding = (childrenPositionAgg + child.clientWidth) / carouselWidth;
            if ((percentageSlideBeginning < elementBeginning ||
                Math.abs(percentageSlideBeginning - elementBeginning) < 0.015) &&
                (elementEnding < percentageSlideEnding ||
                    Math.abs(elementEnding - percentageSlideEnding) < 0.015)) {
                visibleEls.push(i);
            }
            childrenPositionAgg += child.clientWidth;
        });
        return visibleEls;
    }, []);
    var handleScroll = useCallback(function (event) {
        var target = event.target;
        var scrollLeft = target.scrollLeft, clientWidth = target.clientWidth, scrollWidth = target.scrollWidth;
        var scrollEnd = Math.round(scrollLeft) + clientWidth >= scrollWidth - 1;
        var scrollStart = scrollLeft === 0;
        if (scrollStart) {
            setScrollingXStatus('start');
        }
        else if (scrollEnd) {
            setScrollingXStatus('end');
        }
        else {
            setScrollingXStatus('scrolling');
        }
        setVisibleElements(updateVisibleSlides({ scrollLeft: scrollLeft, clientWidth: clientWidth, scrollWidth: scrollWidth }));
        // Calculate slide index based on scroll position
        var slideIndex = slides.findIndex(function (slide, index) {
            if (scrollEnd) {
                return index === slides.length - 1;
            }
            return scrollLeft + 1 < slide.offset + slide.itemsWidth;
        });
        setCurrentSlide(slideIndex);
    }, [slides, updateVisibleSlides]);
    useEffect(function () {
        var _a;
        var scrollElement = (_a = scrollBarRef.current) === null || _a === void 0 ? void 0 : _a.getScrollElement();
        scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.addEventListener('scroll', handleScroll, { passive: true });
        return function () { return scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.removeEventListener('scroll', handleScroll); };
    }, [handleScroll, scrollBarRef]);
    return {
        children: children,
        childrenCount: childrenCount,
        slides: slides,
        currentSlide: currentSlide,
        buttonRefs: buttonRefs,
        carouselRef: carouselRef,
        carouselInnerRef: carouselInnerRef,
        handleScroll: handleScroll,
        scrollingXStatus: scrollingXStatus,
        scrollBarRef: scrollBarRef,
        scrollToNextSlide: scrollToNextSlide,
        scrollToPreviousSlide: scrollToPreviousSlide,
        handleTouchStart: handleTouchStart,
        handleTouchEnd: handleTouchEnd,
        handleKeyDown: handleKeyDown,
    };
};
export default useCarousel;
//# sourceMappingURL=useCarousel.js.map